<template>
    <div class="asn-selector" :class="{ 'is-open': dropdownOpen }">
        <div class="asn-control" @mousedown="onControlClick">
            <span v-for="article in selected" :key="article.id" class="asn-chip">
                <span v-if="article.number" class="asn-chip-number">{{ article.number }}</span>
                <span class="asn-chip-label">{{ article.name }}</span>
                <i class="far fa-times asn-chip-remove" title="Retirer" @click.stop="removeArticle(article)"></i>
            </span>
            <input ref="input"
                   type="text"
                   class="asn-input"
                   v-model="query"
                   :placeholder="selected.length === 0 ? placeholder : ''"
                   autocomplete="off"
                   @input="onQueryInput"
                   @focus="dropdownOpen = true"
                   @keydown.enter.prevent="onEnter"
                   @keydown.down.prevent="highlight(1)"
                   @keydown.up.prevent="highlight(-1)"
                   @keydown.esc.prevent="closeDropdown"
                   @keydown.delete="onDeleteKey">
            <i v-show="loading" class="far fa-circle-notch fa-spin asn-spinner"></i>
            <i v-show="!loading && selected.length > 0" class="far fa-times-circle asn-clear" title="Tout retirer" @click.stop="clearAll"></i>
        </div>

        <ul class="asn-dropdown" v-show="dropdownOpen && results.length > 0">
            <li v-for="(item, index) in results"
                :key="item.id"
                class="asn-option"
                :class="{ 'is-highlighted': index === highlighted }"
                @mousedown.prevent="selectArticle(item)"
                @mouseenter="highlighted = index">
                <span v-if="item.number" class="asn-option-number">{{ item.number }}</span>
                <span class="asn-option-name">{{ item.name }}</span>
                <span v-for="sn in item.matched_serial_numbers" :key="'sn-' + sn" class="asn-option-sn">NS : {{ sn }}</span>
                <span v-if="item.reference" class="asn-option-ref">{{ item.reference }}</span>
            </li>
        </ul>

        <div class="asn-no-result" v-show="dropdownOpen && !loading && query.trim().length >= minChars && results.length === 0">
            Aucun article trouvé pour « {{ query.trim() }} »
        </div>
    </div>
</template>

<script>
export default {
    name: 'article-serial-number-component',
    props: {
        // Id du select — le champ hidden cible est "{id}-input-target"
        id: { type: String, required: true },
        // URL de l'endpoint autocomplete
        route: { type: String, required: true },
        // IDs présélectionnés (séparés par ";" ou ",") — ex : old('article_ids')
        initialIds: { type: String, default: '' },
        placeholder: { type: String, default: 'Rechercher un article...' },
        minChars: { type: Number, default: 2 },
        maxResults: { type: Number, default: 15 },
    },
    data() {
        return {
            query: '',
            results: [],
            selected: [],
            highlighted: -1,
            loading: false,
            dropdownOpen: false,
            debounceTimer: null,
        };
    },
    methods: {
        onControlClick() {
            this.focusInput();
        },
        focusInput() {
            this.$nextTick(() => {
                if (this.$refs.input) this.$refs.input.focus();
            });
        },
        onQueryInput() {
            this.highlighted = -1;
            clearTimeout(this.debounceTimer);
            const term = this.query.trim();
            if (term.length < this.minChars) {
                this.results = [];
                return;
            }
            this.debounceTimer = setTimeout(() => this.search(term), 250);
        },
        buildUrl(term) {
            const params = [];
            if (term) params.push('q=' + encodeURIComponent(term));
            params.push('maxResults=' + this.maxResults);
            params.push('with_serial_numbers=1');
            const excluded = this.selected.map(a => a.id).join(';');
            if (excluded) params.push('!id=' + encodeURIComponent(excluded));
            return this.route + '?' + params.join('&');
        },
        async search(term) {
            this.loading = true;
            try {
                const response = await fetch(this.buildUrl(term), this.getHttpJsonRequestHeaders());
                const json = await response.json();
                this.results = (json.data || []).filter(item => !this.selected.some(a => a.id === item.id));
                this.highlighted = this.results.length > 0 ? 0 : -1;
            } catch (e) {
                this.results = [];
            }
            this.loading = false;
        },
        async onEnter() {
            const term = this.query.trim();
            if (this.results.length > 0) {
                const index = this.highlighted >= 0 ? this.highlighted : 0;
                this.selectArticle(this.results[index]);
            } else if (term.length >= this.minChars) {
                // Workflow scanner : recherche immédiate puis sélection du premier résultat
                await this.search(term);
                if (this.results.length > 0) {
                    this.selectArticle(this.results[0]);
                }
            }
        },
        selectArticle(item) {
            if (!this.selected.some(a => a.id === item.id)) {
                this.selected.push(item);
            }
            this.query = '';
            this.results = [];
            this.highlighted = -1;
            this.syncHiddenInput();
            this.focusInput();
        },
        removeArticle(article) {
            this.selected = this.selected.filter(a => a.id !== article.id);
            this.syncHiddenInput();
            this.focusInput();
        },
        clearAll() {
            this.selected = [];
            this.syncHiddenInput();
            this.focusInput();
        },
        onDeleteKey() {
            if (this.query === '' && this.selected.length > 0) {
                this.removeArticle(this.selected[this.selected.length - 1]);
            }
        },
        highlight(delta) {
            if (this.results.length === 0) return;
            const length = this.results.length;
            this.highlighted = ((this.highlighted + delta) % length + length) % length;
            this.$nextTick(() => {
                const options = this.$el.querySelectorAll('.asn-option');
                const el = options[this.highlighted];
                if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
            });
        },
        closeDropdown() {
            this.dropdownOpen = false;
        },
        syncHiddenInput() {
            const el = document.getElementById(this.id + '-input-target');
            if (el) el.value = this.selected.map(a => a.id).join(';');
        },
        onDocumentClick(e) {
            if (!this.$el.contains(e.target)) this.closeDropdown();
        },
        async restoreInitial() {
            const ids = String(this.initialIds || '').split(/[;,]/).map(s => s.trim()).filter(Boolean);
            if (ids.length === 0) return;
            try {
                const response = await fetch(
                    this.route + '?article_id=' + encodeURIComponent(ids.join(',')),
                    this.getHttpJsonRequestHeaders()
                );
                const json = await response.json();
                (json.data || []).forEach(item => {
                    if (!this.selected.some(a => a.id === item.id)) this.selected.push(item);
                });
            } catch (e) {
                // En cas d'échec, le champ hidden conserve les IDs d'origine
            }
        },
    },
    mounted() {
        document.addEventListener('click', this.onDocumentClick);
        this.restoreInitial();
    },
    beforeDestroy() {
        document.removeEventListener('click', this.onDocumentClick);
        clearTimeout(this.debounceTimer);
    },
};
</script>

<style scoped>
.asn-selector {
    position: relative;
}
.asn-control {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px;
    min-height: 38px;
    padding: 4px 8px;
    background: #fff;
    border: 1px solid #ced4da;
    border-radius: .25rem;
    cursor: text;
    transition: border-color .15s ease-in-out, box-shadow .15s ease-in-out;
}
.asn-control:focus-within {
    border-color: #80bdff;
    box-shadow: 0 0 0 .2rem rgba(0, 123, 255, .25);
}
.asn-selector.is-open .asn-control {
    border-radius: .25rem .25rem 0 0;
}
.asn-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    max-width: 100%;
    padding: 3px 8px;
    border-radius: 12px;
    background: #eaf2fb;
    border: 1px solid #bcd7f0;
    color: #1f4e79;
    font-size: 13px;
    line-height: 1.5;
}
.asn-chip-number {
    flex-shrink: 0;
    padding: 0 6px;
    border-radius: 8px;
    background: #1f6fb2;
    color: #fff;
    font-size: 11px;
    font-weight: 600;
}
.asn-chip-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.asn-chip-remove {
    flex-shrink: 0;
    cursor: pointer;
    color: #6c8bab;
    font-size: 12px;
}
.asn-chip-remove:hover {
    color: #d9534f;
}
.asn-input {
    flex: 1;
    min-width: 140px;
    border: 0;
    outline: 0;
    padding: 4px 2px;
    font-size: 14px;
    background: transparent;
}
.asn-spinner {
    color: #6c757d;
    font-size: 13px;
}
.asn-clear {
    cursor: pointer;
    color: #adb5bd;
    font-size: 15px;
}
.asn-clear:hover {
    color: #d9534f;
}
.asn-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 1000;
    margin: 0;
    padding: 4px 0;
    list-style: none;
    background: #fff;
    border: 1px solid #80bdff;
    border-top: 0;
    border-radius: 0 0 .25rem .25rem;
    box-shadow: 0 6px 12px rgba(0, 0, 0, .12);
    max-height: 260px;
    overflow-y: auto;
}
.asn-option {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    cursor: pointer;
    font-size: 14px;
}
.asn-option.is-highlighted {
    background: #eaf2fb;
}
.asn-option-number {
    flex-shrink: 0;
    padding: 1px 6px;
    border-radius: 4px;
    background: #6c757d;
    color: #fff;
    font-size: 12px;
    font-weight: 600;
}
.asn-option-name {
    font-weight: 500;
}
.asn-option-sn {
    flex-shrink: 0;
    padding: 1px 6px;
    border-radius: 4px;
    background: #d1ecf1;
    color: #0c5460;
    font-size: 12px;
}
.asn-option-ref {
    margin-left: auto;
    flex-shrink: 0;
    color: #6c757d;
    font-size: 13px;
}
.asn-no-result {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 1000;
    padding: 10px 12px;
    background: #fff;
    border: 1px solid #80bdff;
    border-top: 0;
    border-radius: 0 0 .25rem .25rem;
    color: #6c757d;
    font-size: 13px;
    box-shadow: 0 6px 12px rgba(0, 0, 0, .12);
}
</style>
